import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Ticket, Check } from "lucide-react";
import {
  getClassDropSettingsApi,
  updateClassDropSettingsApi,
  getClassDropClassTypesApi,
  setClassDropEligibleClassApi,
} from "../../../api/apiEndpoints";

const formatSchedule = (nextClassStartTime: string | null, upcomingCount: number): string => {
  if (!nextClassStartTime) return "";
  const date = new Date(nextClassStartTime);
  const dateLabel = date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const timeLabel = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const extra = upcomingCount > 1 ? ` · +${upcomingCount - 1} more upcoming` : "";
  return `Next: ${dateLabel}, ${timeLabel}${extra}`;
};

const RELEASE_WINDOW_OPTIONS = [24, 36, 48, 72];
const MAX_SPOTS_OPTIONS = [2, 3, 4, 5, 6];
const MIN_SPOTS_OPTIONS = [2, 3, 4, 5, 6];
const LAST_MINUTE_FILL_HOURS_OPTIONS = [4, 12, 18, 24];
const KEEP_RESERVED_OPTIONS = [0, 1, 2];

const KEEP_RESERVED_LABELS: Record<number, string> = {
  0: "Fill all remaining spots",
  1: "Keep 1 spot",
  2: "Keep 2 spots",
};

const PRESET_LABELS: Record<string, { label: string; blurb: string }> = {
  rapid_growth: { label: "Rapid Growth", blurb: "Most exposure — more spots, wider release." },
  steady_inflow: { label: "Steady Inflow", blurb: "Balanced flow of new faces." },
  low_slow: { label: "Low & Slow", blurb: "Most protective of your paid inventory." },
};

const FIRST_TIME_LABELS: Record<string, string> = {
  first_time_only: "First-timers only",
  allow_second: "Allow 2nd visit",
  off: "Off",
};

interface Settings {
  enabled: boolean;
  mode: string;
  maxJrSpotsPerClass: number;
  releaseWindowHours: number;
  minSpotsRemaining: number;
  firstTimeMode: string;
  lastMinuteFillEnabled: boolean;
  lastMinuteFillHours: number;
  lastMinuteFillKeepReserved: number;
}

const DEFAULTS: Settings = {
  enabled: false,
  mode: "low_slow",
  maxJrSpotsPerClass: 2,
  releaseWindowHours: 24,
  minSpotsRemaining: 5,
  firstTimeMode: "first_time_only",
  lastMinuteFillEnabled: false,
  lastMinuteFillHours: 12,
  lastMinuteFillKeepReserved: 1,
};

interface Props {
  businessId?: string;
}

const ClassDropsSettings = ({ businessId }: Props) => {
  const [form, setForm] = useState<Settings>(DEFAULTS);
  const [presets, setPresets] = useState<Record<string, any>>({});
  const [classTypes, setClassTypes] = useState<any[]>([]);
  const [classProvider, setClassProvider] = useState<string | null>(null);
  const [classTypesLoading, setClassTypesLoading] = useState(false);
  const [togglingType, setTogglingType] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!businessId) return;
    setLoading(true);
    setError(null);
    getClassDropSettingsApi(businessId)
      .then((res) => {
        if (cancelled) return;
        const s = res.data?.settings;
        if (s) {
          setForm({
            enabled: Boolean(s.enabled),
            mode: s.mode ?? "low_slow",
            maxJrSpotsPerClass: s.maxJrSpotsPerClass ?? 2,
            releaseWindowHours: s.releaseWindowHours ?? 24,
            minSpotsRemaining: s.minSpotsRemaining ?? 5,
            firstTimeMode: s.firstTimeMode ?? "first_time_only",
            lastMinuteFillEnabled: Boolean(s.lastMinuteFillEnabled),
            lastMinuteFillHours: s.lastMinuteFillHours ?? 12,
            lastMinuteFillKeepReserved: s.lastMinuteFillKeepReserved ?? 1,
          });
        } else {
          setForm(DEFAULTS);
        }
        setPresets(res.data?.presets ?? {});
      })
      .catch(() => !cancelled && setError("Could not load Class Drop settings."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  useEffect(() => {
    let cancelled = false;
    if (!businessId) return;
    setClassTypesLoading(true);
    getClassDropClassTypesApi(businessId)
      .then((res) => {
        if (cancelled) return;
        setClassProvider(res.data?.provider ?? null);
        setClassTypes(res.data?.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setClassTypes([]);
      })
      .finally(() => !cancelled && setClassTypesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  const toggleClassType = async (item: any) => {
    if (!businessId || !classProvider) return;
    const next = !item.eligible;
    setTogglingType(item.externalClassTypeId);
    setClassTypes((prev) =>
      prev.map((c) =>
        c.externalClassTypeId === item.externalClassTypeId ? { ...c, eligible: next } : c
      )
    );
    try {
      await setClassDropEligibleClassApi({
        businessId,
        provider: classProvider,
        externalClassTypeId: item.externalClassTypeId,
        className: item.className ?? undefined,
        eligible: next,
      });
    } catch {
      setClassTypes((prev) =>
        prev.map((c) =>
          c.externalClassTypeId === item.externalClassTypeId ? { ...c, eligible: !next } : c
        )
      );
      setError("Could not update class eligibility.");
    } finally {
      setTogglingType(null);
    }
  };

  const applyPreset = (mode: string) => {
    const p = presets[mode];
    if (p) {
      setForm((f) => ({
        ...f,
        mode,
        maxJrSpotsPerClass: p.maxJrSpotsPerClass,
        releaseWindowHours: p.releaseWindowHours,
        minSpotsRemaining: p.minSpotsRemaining,
        firstTimeMode: p.firstTimeMode,
        lastMinuteFillEnabled: p.lastMinuteFillEnabled,
        lastMinuteFillHours: p.lastMinuteFillHours,
        lastMinuteFillKeepReserved: p.lastMinuteFillKeepReserved,
      }));
    } else {
      setForm((f) => ({ ...f, mode }));
    }
    setSavedAt(null);
  };

  const set = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSavedAt(null);
  };

  const toggleEnabled = async () => {
    if (!businessId || saving) return;
    const next = { ...form, enabled: !form.enabled };
    setForm(next);
    setSaving(true);
    setError(null);
    try {
      await updateClassDropSettingsApi({ businessId, ...next });
      setSavedAt(Date.now());
    } catch {
      setForm((f) => ({ ...f, enabled: !next.enabled }));
      setError("Could not update Class Drops. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!businessId) return;
    setSaving(true);
    setError(null);
    try {
      await updateClassDropSettingsApi({ businessId, ...form });
      setSavedAt(Date.now());
    } catch {
      setError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const presetKeys = useMemo(
    () => Object.keys(presets).length ? Object.keys(presets) : Object.keys(PRESET_LABELS),
    [presets]
  );

  if (!businessId) {
    return <Panel><Muted>Select a location to configure Class Drops.</Muted></Panel>;
  }

  return (
    <Wrap>
      <Panel>
        <Header>
          <IconCircle><Ticket size={20} /></IconCircle>
          <div>
            <Title>JackedRabbit Class Drops</Title>
            <Sub>Offer free first classes to nearby JR members and turn them into trials and members.</Sub>
          </div>
          <Toggle
            role="switch"
            aria-checked={form.enabled}
            $on={form.enabled}
            onClick={toggleEnabled}
          >
            <Knob $on={form.enabled} />
          </Toggle>
        </Header>

        {loading ? (
          <Muted>Loading…</Muted>
        ) : (
          <>
            <SectionLabel>Mode</SectionLabel>
            <PresetRow>
              {presetKeys.map((key) => {
                const meta = PRESET_LABELS[key] ?? { label: key, blurb: "" };
                const active = form.mode === key;
                return (
                  <PresetCard key={key} $active={active} onClick={() => applyPreset(key)}>
                    <PresetTitle>{meta.label}</PresetTitle>
                    <PresetBlurb>{meta.blurb}</PresetBlurb>
                    {active && <PresetCheck><Check size={14} /></PresetCheck>}
                  </PresetCard>
                );
              })}
            </PresetRow>

            <Grid>
              <Field>
                <FieldLabel>Max JR spots / class</FieldLabel>
                <Select
                  value={form.maxJrSpotsPerClass}
                  onChange={(e) => set("maxJrSpotsPerClass", Number(e.target.value))}
                >
                  {MAX_SPOTS_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                </Select>
              </Field>

              <Field>
                <FieldLabel>Initial release</FieldLabel>
                <Select
                  value={form.releaseWindowHours}
                  onChange={(e) => set("releaseWindowHours", Number(e.target.value))}
                >
                  {RELEASE_WINDOW_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n} hours before</option>
                  ))}
                </Select>
              </Field>

              <Field>
                <FieldLabel>Only release if ≥ spots remain</FieldLabel>
                <Select
                  value={form.minSpotsRemaining}
                  onChange={(e) => set("minSpotsRemaining", Number(e.target.value))}
                >
                  {MIN_SPOTS_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                </Select>
              </Field>

              <Field>
                <FieldLabel>First-time visitors</FieldLabel>
                <Segmented>
                  {Object.keys(FIRST_TIME_LABELS).map((k) => (
                    <Seg
                      key={k}
                      $on={form.firstTimeMode === k}
                      onClick={() => set("firstTimeMode", k)}
                    >
                      {FIRST_TIME_LABELS[k]}
                    </Seg>
                  ))}
                </Segmented>
              </Field>
            </Grid>

            <LMFPanel>
              <LMFHeader>
                <div>
                  <LMFTitle>Last-Minute Fill</LMFTitle>
                  <LMFSub>
                    Fill seats that would otherwise go unused by releasing additional inventory
                    closer to class time.
                  </LMFSub>
                </div>
                <Toggle
                  role="switch"
                  aria-checked={form.lastMinuteFillEnabled}
                  $on={form.lastMinuteFillEnabled}
                  onClick={() => set("lastMinuteFillEnabled", !form.lastMinuteFillEnabled)}
                >
                  <Knob $on={form.lastMinuteFillEnabled} />
                </Toggle>
              </LMFHeader>

              <Grid>
                <Field>
                  <FieldLabel>Release additional spots</FieldLabel>
                  <Select
                    value={form.lastMinuteFillHours}
                    disabled={!form.lastMinuteFillEnabled}
                    onChange={(e) => set("lastMinuteFillHours", Number(e.target.value))}
                  >
                    {LAST_MINUTE_FILL_HOURS_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n} hours before</option>
                    ))}
                  </Select>
                </Field>

                <Field>
                  <FieldLabel>Keep reserved for studio</FieldLabel>
                  <Select
                    value={form.lastMinuteFillKeepReserved}
                    disabled={!form.lastMinuteFillEnabled}
                    onChange={(e) => set("lastMinuteFillKeepReserved", Number(e.target.value))}
                  >
                    {KEEP_RESERVED_OPTIONS.map((n) => (
                      <option key={n} value={n}>{KEEP_RESERVED_LABELS[n]}</option>
                    ))}
                  </Select>
                </Field>
              </Grid>

              <InfoNote>
                At the selected time, JackedRabbit can open additional unsold inventory beyond the
                initial JR cap while keeping the reserved number for the studio.
              </InfoNote>
            </LMFPanel>

            <SectionLabel>Eligible classes</SectionLabel>
            {classTypesLoading ? (
              <Muted>Loading your class catalog…</Muted>
            ) : classTypes.length === 0 ? (
              <Muted>
                No upcoming classes found to configure. Once classes are scheduled on your booking
                platform, the class types you offer will appear here to enable for drops.
              </Muted>
            ) : (
              <ClassList>
                {classTypes.map((c) => (
                  <ClassRow key={c.externalClassTypeId}>
                    <ClassInfo>
                      <span>{c.className || c.externalClassTypeId}</span>
                      <ClassSchedule>{formatSchedule(c.nextClassStartTime, c.upcomingCount)}</ClassSchedule>
                    </ClassInfo>
                    <Toggle
                      role="switch"
                      aria-checked={c.eligible}
                      $on={c.eligible}
                      disabled={togglingType === c.externalClassTypeId}
                      onClick={() => toggleClassType(c)}
                      style={{ marginLeft: "auto" }}
                    >
                      <Knob $on={c.eligible} />
                    </Toggle>
                  </ClassRow>
                ))}
              </ClassList>
            )}

            <Footer>
              {error && <ErrorText>{error}</ErrorText>}
              {savedAt && !error && <SavedText><Check size={14} /> Saved</SavedText>}
              <SaveBtn onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </SaveBtn>
            </Footer>
          </>
        )}
      </Panel>
    </Wrap>
  );
};

export default ClassDropsSettings;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Panel = styled.div`
  background: #ffffff;
  border: 1px solid #ece4da;
  border-radius: 16px;
  padding: 24px;
  font-family: 'Aileron', 'Poppins', sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const IconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #f2efe9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9a8e84;
  flex: 0 0 auto;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #111111;
  margin: 0;
`;

const Sub = styled.p`
  font-size: 13.5px;
  color: #8a7f74;
  margin: 4px 0 0;
  max-width: 460px;
  line-height: 1.5;
`;

const Toggle = styled.button<{ $on: boolean }>`
  margin-left: auto;
  width: 48px;
  height: 28px;
  border-radius: 16px;
  border: none;
  cursor: pointer;
  background: ${(p) => (p.$on ? "#3a7d44" : "#d8d0c4")};
  position: relative;
  transition: background 0.18s;
  flex: 0 0 auto;
`;

const Knob = styled.span<{ $on: boolean }>`
  position: absolute;
  top: 3px;
  left: ${(p) => (p.$on ? "23px" : "3px")};
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  transition: left 0.18s;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: #9a8e84;
  margin: 24px 0 12px;
`;

const PresetRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const PresetCard = styled.button<{ $active: boolean }>`
  position: relative;
  text-align: left;
  border-radius: 14px;
  padding: 14px 16px;
  cursor: pointer;
  background: ${(p) => (p.$active ? "#111111" : "#faf8f4")};
  border: 1.5px solid ${(p) => (p.$active ? "#111111" : "#ece4da")};
  color: ${(p) => (p.$active ? "#ffffff" : "#111111")};
  transition: all 0.15s;

  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid ${(p) => (p.$active ? "#ffffff" : "#111111")};
    outline-offset: 2px;
  }
`;

const PresetTitle = styled.div<{ children?: any }>`
  font-size: 14.5px;
  font-weight: 700;
  color: inherit;
`;

const PresetBlurb = styled.div`
  font-size: 12px;
  margin-top: 4px;
  opacity: 0.72;
`;

const PresetCheck = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  color: #fff;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 8px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #4a4038;
`;

const Select = styled.select`
  border: 1px solid #ece4da;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  background: #fff;
  color: #111;
  outline: none;

  &:focus {
    border-color: #b9ac9c;
  }

  &:disabled {
    background: #faf8f4;
    color: #b3a99d;
    cursor: default;
  }
`;

const LMFPanel = styled.div`
  margin-top: 24px;
  border: 1px solid #ece4da;
  border-radius: 14px;
  padding: 18px 20px;
  background: #faf8f4;
`;

const LMFHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const LMFTitle = styled.div`
  font-size: 14.5px;
  font-weight: 700;
  color: #111111;
`;

const LMFSub = styled.p`
  font-size: 12.5px;
  color: #8a7f74;
  margin: 4px 0 0;
  max-width: 460px;
  line-height: 1.5;
`;

const InfoNote = styled.p`
  display: flex;
  font-size: 12px;
  color: #9a8e84;
  line-height: 1.5;
  margin: 14px 0 0;
`;

const Segmented = styled.div`
  display: flex;
  gap: 4px;
  background: #f4f4f6;
  border: 1px solid #e4e4e8;
  border-radius: 12px;
  padding: 4px;
`;

const Seg = styled.button<{ $on: boolean }>`
  flex: 1;
  border: none;
  border-radius: 9px;
  padding: 8px 6px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  color: ${(p) => (p.$on ? "#fff" : "#8a7f74")};
  background: ${(p) => (p.$on ? "#111111" : "transparent")};
  transition: all 0.15s;
`;

const ClassList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #ece4da;
  border-radius: 12px;
  overflow: hidden;
`;

const ClassRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  font-size: 14px;
  color: #111;

  & + & {
    border-top: 1px solid #f2ede5;
  }
`;

const ClassInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ClassSchedule = styled.span`
  font-size: 12px;
  color: #9a8e84;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 24px;
  justify-content: flex-end;
`;

const SaveBtn = styled.button`
  border: none;
  border-radius: 24px;
  padding: 12px 22px;
  background: #111111;
  color: #fff;
  font-size: 14.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const SavedText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #3a7d44;
  font-size: 13px;
  font-weight: 600;
`;

const ErrorText = styled.span`
  color: #9c4a3c;
  font-size: 13px;
  font-weight: 600;
`;

const Muted = styled.p`
  font-size: 13.5px;
  color: #8a7f74;
  line-height: 1.5;
  margin: 0;
`;
